import { message } from '@/components/Message';
import Modal from '@/components/Modal';
import PosButton from '@/components/PosButton';
import Price from '@/components/Price';
import { PreOrder } from '@/models/order';
import { calcSpecificationsTotalNumber, calcSpecificationsTotalPrice } from '@/models/pos';
import { playSound } from '@/utils/sound';
import { modalPrompt, useSkuImg } from '@/utils/uiHelper';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  RollbackOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { Avatar, Empty, List, Space, Typography } from 'antd';
import BigNumber from 'bignumber.js';
import React from 'react';
import { usePreOrderStore } from '../store/usePreOrderStore';
import { useSettlementStore } from '../Settlement/store/useSettlementStore';
import styles from './style.less';

type Prop = {
  onRequestDeletePreOrder: (preorder: PreOrder) => void;
  onRequestPushToStation: () => void;
};
const CheckoutArea: React.FC<Prop> = ({ onRequestPushToStation, onRequestDeletePreOrder }: Prop) => {
  const getSkuImg = useSkuImg();
  const preOrder = usePreOrderStore(s => s.preOrder);

  const checkout = () => {
    if (useSettlementStore.getState().checkout()) {
      playSound('click_light');
    } else {
      message.error('请添加要结账的商品');
      playSound('error');
    }
  };

  const specifications = preOrder.specifications || [];

  const isNotEmpty = specifications.length > 0;

  /** 修改数量（含库存校验提示），供 +/- 按钮使用 */
  const changeNumber = (id: number, num: number) => {
    if (!usePreOrderStore.getState().changeSpecificationNumber(id, num)) {
      message.destroy();
      message.error('该商品剩余库存不足');
      playSound('error');
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.head}>
        <div className={styles.headLeft}>
          {isNotEmpty && (
            <PosButton
              type={'text'}
              danger
              className={styles.headBtn}
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  autoFocusButton: 'ok',
                  cancelButtonProps: { size: 'large' },
                  okButtonProps: { size: 'large' },
                  title: '清空',
                  icon: <ExclamationCircleOutlined />,
                  content: '请确认该操作',
                  onOk() {
                    usePreOrderStore.getState().clearSpecifications();
                  },
                });
              }}>
              清空
            </PosButton>
          )}
        </div>
        <div className={styles.headCenter}>
          <ShoppingCartOutlined /> 结账台
        </div>
        <div className={styles.headRight}>
          {preOrder.key ? (
            <PosButton
              type={'text'}
              danger
              icon={<RollbackOutlined />}
              className={styles.headBtn}
              onClick={() => {
                onRequestDeletePreOrder(preOrder);
              }}>
              撤单
            </PosButton>
          ) : null}
        </div>
      </h3>
      <div className={styles.center}>
        <div className={styles.skuList}>
          {isNotEmpty ? (
            <List
              size="small"
              itemLayout="vertical"
              bordered={false}
              dataSource={specifications}
              renderItem={item => (
                <List.Item>
                  <div className={styles.listItem}>
                    <PosButton
                      className={styles.listItemDelete}
                      shape={'circle'}
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => {
                        usePreOrderStore.getState().deleteSpecification(item.id);
                      }}
                    />
                    <h4 className={styles.listItemHead}>
                      <Space size={5}>
                        <Avatar
                          src={`${getSkuImg(item.picUrl || item.goodsPicUrl, 50)}`}
                          shape={'square'}
                          draggable={false}
                        />
                        <div>
                          <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0, lineHeight: 1.3 }}>
                            {item.goodsName}
                          </Typography.Paragraph>
                          {item.name && (
                            <div>
                              <Typography.Text type="secondary">{item.name}</Typography.Text>
                            </div>
                          )}
                        </div>
                      </Space>
                    </h4>
                    <div className={'space-between'}>
                      <Space size={5}>
                        <PosButton
                          shape={'circle'}
                          icon={<MinusOutlined />}
                          onClick={() => {
                            if (item.number === 1) {
                              usePreOrderStore.getState().deleteSpecification(item.id);
                            } else {
                              changeNumber(item.id, item.number - 1);
                            }
                          }}
                        />
                        <PosButton
                          shape={'circle'}
                          onClick={() => {
                            modalPrompt({
                              title: '请输入数量',
                              desc: (
                                <Typography.Paragraph>
                                  库存剩余:{' '}
                                  <Typography.Text strong type={'warning'}>
                                    {item.stock}
                                  </Typography.Text>
                                </Typography.Paragraph>
                              ),
                              onOk: v => {
                                if (!isNaN(v) && v > 0) {
                                  const ok = usePreOrderStore
                                    .getState()
                                    .changeSpecificationNumber(item.id, Math.floor(+v));
                                  if (!ok) {
                                    message.destroy();
                                    message.error('该商品剩余库存不足');
                                    playSound('error');
                                  }
                                  return ok;
                                } else {
                                  playSound('error');
                                  message.error('请输入大于1的整数');
                                  return false;
                                }
                              },
                              useNumberKeyboard: true,
                              numberKeyboardProps: {
                                precision: 0,
                                showAllClean: true,
                              },
                            });
                          }}>
                          {item.number}
                        </PosButton>
                        <PosButton
                          shape={'circle'}
                          icon={<PlusOutlined />}
                          onClick={() => {
                            changeNumber(item.id, item.number + 1);
                          }}
                        />
                      </Space>

                      <Space direction={'vertical'} size={0}>
                        <span>
                          单价: <Price type={'secondary'} value={new BigNumber(item.price).toFixed(2)} />
                        </span>

                        <span>
                          小计:{' '}
                          <Price
                            type={'secondary'}
                            value={new BigNumber(item.price).multipliedBy(item.number).toFixed(2)}
                          />
                        </span>
                      </Space>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <Empty
              image={require('./empty.png')}
              className={`empty-large ${styles.emptyArea}`}
              description={<>点选或扫码添加</>}
            />
          )}
        </div>
        {isNotEmpty && (
          <div className={styles.summary}>
            <div>
              合计:{' '}
              <Typography.Text strong className="font-monospace">
                {calcSpecificationsTotalNumber(specifications)}
              </Typography.Text>
              件
            </div>
            <div>
              金额: <Price style={{ fontSize: 16 }} value={calcSpecificationsTotalPrice(specifications)} />
            </div>
          </div>
        )}
      </div>
      <div className={styles.foot}>
        <div className={`${styles.item}`} onClick={onRequestPushToStation}>
          {preOrder.key ? '继续服务' : '创建服务'}
        </div>
        <div className={`${styles.checkout} ${styles.item}`} onClick={checkout}>
          结账
        </div>
      </div>
    </div>
  );
};

export default CheckoutArea;
